"use client";

import { useLayoutEffect } from "react";
import {
  readThemePreferenceFromCookie,
  resolveThemeMode,
  systemPrefersDarkMode,
  type ResolvedThemeMode,
  type ThemePreference,
} from "./theme-preference";

function applyDocumentTheme(resolvedMode: ResolvedThemeMode) {
  document.documentElement.classList.toggle("dark", resolvedMode === "dark");
  document.documentElement.style.colorScheme =
    resolvedMode === "dark" ? "dark" : "light";
}

type ThemeDocumentSyncProps = {
  defaultPreference: ThemePreference;
};

/** Keeps `.dark` on <html> in sync before paint without touching React theme state. */
export function ThemeDocumentSync({ defaultPreference }: ThemeDocumentSyncProps) {
  useLayoutEffect(() => {
    const preference = readThemePreferenceFromCookie(defaultPreference);
    const apply = () => {
      applyDocumentTheme(resolveThemeMode(preference, systemPrefersDarkMode()));
    };

    apply();

    if (preference !== "system") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => {
      applyDocumentTheme(resolveThemeMode("system", systemPrefersDarkMode()));
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [defaultPreference]);

  return null;
}
